import React, { FC, useCallback, useState, useEffect } from "react";

import {
    EmailInput,
    validateEmail,
    OnboardingAside,
    OnboardingContent,
    PrimaryAlternateControl,
    AutoCompleteSuggestionsInput,
    Suggestion,
    LoadingSpinner,
} from "@xapp/components";

import { INTEGRATIONS } from "components/Integrations/components/IntegrationDropdown";

import { OnboardingProps } from "../../models";

import classes from "./LeadDestination.module.scss";

export interface LeadDestinationStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;
    /**
     * Optional aside content
     */
    aside?: React.ReactNode;
    /**
     * Site data used to pre-populate fields
     */
    initialProps?: OnboardingProps;
    /**
     * Disable all integrations & interactions (except "next" & "previous")
     */
    disabled?: boolean;
    /**
     * Loading state while saving
     */
    loading?: boolean;
    /**
     * Determine how we display the FSM list
     */
    fsmListStyle?: "dropdown" | "icons";
    /**
     * When provided, it will imit the integrations to those in the
     */
    limitIntegrations?: string[];
    /**
     * User is ready to save everything!
     */
    onNext(app: Pick<OnboardingProps, "leadsContact" | "beta">): void;
    /**
     * User wants to go back to the previous step
     */
    onBack?(): void;
}

export const LeadDestinationStep: FC<LeadDestinationStepProps> = (props) => {
    const { aside, style, disabled, loading, onBack, onNext, initialProps, className, limitIntegrations } = props;

    const [leadsContact, setLeadsContact] = useState<string>(initialProps?.leadsContact || "");
    const [leadsContactValid, setLeadsContactValid] = useState<boolean>(
        validateEmail(initialProps?.leadsContact || "")
    );

    const [selectedIntegration, setSelectedIntegration] = useState<Suggestion>();

    useEffect(() => {
        if (initialProps?.leadsContact) {
            setLeadsContact(initialProps.leadsContact);
        }
    }, [initialProps?.leadsContact]);

    const handleSelectedIntegration = useCallback((integration: Suggestion) => {
        setSelectedIntegration(integration);
    }, []);

    const handleLeadsContactUpdated = useCallback((value: string, valid: boolean) => {
        setLeadsContact(value);
        setLeadsContactValid(valid);
    }, []);

    const handleNextClicked = useCallback(() => {
        // if they selected "other" then use the "otherFSM" value

        const leadsIntegration: string = selectedIntegration?.label || undefined;

        onNext({
            ...initialProps,
            leadsContact,
            beta: {
                ...initialProps?.beta,
                onboarding: {
                    ...initialProps?.beta?.onboarding,
                    leadsIntegration: [leadsIntegration],
                },
            },
        });
    }, [selectedIntegration, onNext, initialProps, leadsContact]);

    const canProceed: boolean = !!selectedIntegration || (leadsContact && leadsContactValid);

    const SUGGESTIONS = INTEGRATIONS.map((integration) => ({
        id: integration.id,
        label: integration.name,
    })).filter((integration) => {
        if (limitIntegrations) {
            return limitIntegrations.includes(integration.id);
        }
        return true;
    });

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}
            <OnboardingContent className={className} style={style}>
                {loading ? (
                    <div style={{ padding: "40px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
                        <LoadingSpinner
                            textStyle={{ fontSize: "1em" }}
                            loadingText="Saving lead destination settings..."
                        />
                    </div>
                ) : (
                    <>
                <h2>Where do we send your leads?</h2>

                <p>Bookings, leads and contact requests can be sent where you want them. </p>

                <div className={classes.leadWrapper}>
                    <h4>Field Service Management (FSM)</h4>

                    <p>
                        We can integrate directly into your existing workflow by sending the booking or lead to your
                        existing FSM like Jobber, ServiceTitan, FieldPulse, etc.
                    </p>

                    <AutoCompleteSuggestionsInput
                        label={"Enter your FSM or CRM"}
                        variation="outlined"
                        echo={true}
                        suggestions={SUGGESTIONS}
                        value={selectedIntegration}
                        onChange={handleSelectedIntegration}
                        disabled={disabled}
                    />
                </div>

                <div className={classes.leadWrapper}>
                    <h4>Email</h4>
                    <p>
                        We can send all booking, leads, and contact requests to your email. If you want to send to many
                        people, we recommend using an email alias.
                    </p>
                    <EmailInput
                        variation="outlined"
                        label={"Leads Email"}
                        value={leadsContact}
                        onChange={handleLeadsContactUpdated}
                        disabled={disabled}
                    />
                </div>

                <PrimaryAlternateControl
                    labels={{
                        primary: "Next",
                        alternate: "Back",
                    }}
                    disabled={{ primary: !canProceed || disabled, alternate: disabled }}
                    onPrimary={handleNextClicked}
                    onAlternate={onBack}
                />
                    </>
                )}
            </OnboardingContent>
        </>
    );
};
